import React, { useEffect, useState } from 'react';
import { parse } from '../../util/urlHelpers';
import {
  Page,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  IconSpinner,
  NamedRedirect,
  AdminNav,
  TableComponent,
} from '../../components';
import TransactionItem from './components/TransactionItem';
import { TopbarContainer } from '../../containers';

import { isScrollingDisabled } from '../../ducks/UI.duck';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { isAdminAccount } from '../../util/userHelper';

import { loadTransactions } from './AdminManageTransactionsPage.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';

import css from './AdminManageTransactionsPage.css';
import SearchPanel from './components/SearchPanel';
import { useLocation } from 'react-router-dom';

export const AdminManageTransactionsPage = () => {
  const scrollingDisabled = useSelector(state => isScrollingDisabled(state));
  const isAuthenticated = useSelector(state => state.Auth.isAuthenticated);
  const currentUser = useSelector(state => state.user.currentUser);
  const isAdmin = isAdminAccount(currentUser);
  const transactions = useSelector(state =>
    getMarketplaceEntities(state, state.AMTxPage.transactionRefs)
  );
  const fetchTxInProgress = useSelector(state => state.AMTxPage.fetchInProgress);
  const fetchTxError = useSelector(state => state.AMTxPage.fetchError);
  const pagination = useSelector(state => state.AMTxPage.pagination);

  const intl = useIntl();

  const [openedId, setOpenId] = useState(null);

  const location = useLocation();
  const { ...searchParams } = parse(location.search);

  useEffect(() => {
    if (searchParams.transactionId) {
      setOpenId(searchParams.transactionId);
    }
  }, [searchParams.transactionId]);

  const handleOpenItem = id => {
    if (openedId === id) {
      setOpenId(null);
    } else {
      setOpenId(id);
    }
  };

  const title = intl.formatMessage({ id: 'AdminPage.title' });

  if ((!isAdmin && currentUser && currentUser.id) || !isAuthenticated) {
    return <NamedRedirect name="NotFoundPage" />;
  }

  if (!isAdmin) {
    return (
      <Page title={title} scrollingDisabled={scrollingDisabled}>
        <LayoutSingleColumn>
          <LayoutWrapperTopbar>
            <TopbarContainer currentPage="AdminManageTransactionsPage" />
          </LayoutWrapperTopbar>
          <LayoutWrapperMain>
            <div className={css.loadingWrapper}>
              <IconSpinner />
            </div>
          </LayoutWrapperMain>
          <LayoutWrapperFooter>
            <Footer />
          </LayoutWrapperFooter>
        </LayoutSingleColumn>
      </Page>
    );
  }

  const error = fetchTxError ? (
    <p className={css.error}>
      <FormattedMessage id="AdminManageTransactionsPage.fetchFailed" />
    </p>
  ) : null;

  const noResults =
    !fetchTxInProgress && transactions.length === 0 && !fetchTxError ? (
      <p key="noResults" className={css.noResults}>
        <FormattedMessage id={'AdminManageTransactionsPage.noTxFound'} />
      </p>
    ) : null;

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer currentPage="AdminManageTransactionsPage" />
          <AdminNav selectedPageName="AdminManageTransactionsPage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <div className={css.container}>
            {error}
            <SearchPanel transactions={transactions} />
            <div className={css.pageTitle}>
              <div className={css.pageTitleContent}>
                <h1>
                  <FormattedMessage
                    id="AMTxPage.title"
                    defaultMessage="Kindershare Test transactions"
                  />
                  <sup>{pagination.totalItems}</sup>
                </h1>
              </div>
            </div>
            {fetchTxInProgress && (
              <div className={css.loadingContainer}>
                <IconSpinner />
              </div>
            )}
            {noResults}
            {!fetchTxInProgress && transactions.length > 0 && (
              <div className={css.tableHeader}>
                <span className={css.lastTransition}>
                  {intl.formatMessage({
                    id: 'AMTxPage.lastTransition',
                    defaultMessage: 'Last transition',
                  })}
                </span>
                <span className={css.timeItem}>
                  {intl.formatMessage({ id: 'AMTxPage.createdAt', defaultMessage: 'Created at' })}
                </span>
                <span>
                  {intl.formatMessage({
                    id: 'AMTxPage.lastActivity',
                    defaultMessage: 'Last activity',
                  })}
                </span>
              </div>
            )}
            <TableComponent
              className={fetchTxInProgress ? css.contentLoading : null}
              keyExtractor={item => item.id.uuid}
              data={transactions}
              renderItem={item => {
                return (
                  <TransactionItem
                    item={item}
                    onClick={handleOpenItem}
                    isOpenDetail={openedId === item.id.uuid}
                  />
                );
              }}
              pagination={pagination}
              pageSearchParams={searchParams}
            />
          </div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

AdminManageTransactionsPage.loadData = (params, search) => {
  const queryParams = parse(search);
  const {
    page = 1,
    id,
    userId,
    per_page = 24,
    last_transition: lastTransition,
    customerId,
    providerId,
    listingId,
    transactionId,
  } = queryParams;

  return loadTransactions({
    page,
    perPage: per_page,
    id,
    userId,
    lastTransition,
    customerId,
    providerId,
    listingId,
    transactionId,
  });
};

export default AdminManageTransactionsPage;
